
import React from "react";
import { CreditCard } from "lucide-react";

const PaymentMethodStep = ({
  installationData,
  handleInputChange,
  selectedContractor,
  errors,
}) => {
  return (
    <div className="space-y-8">
      <div className="text-center">
        <CreditCard className="w-16 h-16 text-emerald-600 mx-auto mb-4" />
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Payment Options
        </h2>
        <p className="text-gray-600">Choose your preferred payment method</p>
      </div>

      {/* Project Cost Summary */}
      <div className="bg-gray-50 rounded-2xl p-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-4">
          Project Cost Summary
        </h3>
        <div className="space-y-3">
          <div className="flex justify-between">
            <span className="text-gray-600">Materials & Labor:</span>
            <span className="font-semibold text-gray-900">
              ${selectedContractor.selectedBid.price.toLocaleString()}
            </span>
          </div>
          <div className="flex justify-between">
            <span className="text-gray-600">Permits & Fees:</span>
            <span className="font-semibold text-emerald-600">Included</span>
          </div>
          <div className="border-t border-gray-200 pt-3">
            <div className="flex justify-between">
              <span className="text-xl font-bold text-gray-900">
                Total Project Cost:
              </span>
              <span className="text-3xl font-bold text-emerald-600">
                ${selectedContractor.selectedBid.price.toLocaleString()}
              </span>
            </div>
            {installationData.paymentMethod === "payment-plan" && (
              <div className="flex justify-between mt-2 p-3 bg-emerald-100 rounded-lg">
                <span className="text-lg font-semibold text-emerald-800">
                  30% Down Payment:
                </span>
                <span className="text-2xl font-bold text-emerald-800">
                  $
                  {(selectedContractor.selectedBid.price * 0.3).toLocaleString()}
                </span>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Payment Method */}
      <div>
        <label className="block text-sm font-semibold text-gray-700 mb-4">
          Select Payment Method
        </label>
        <div className="space-y-4">
          {[
            {
              id: "payment-plan",
              label: "Payment Plan",
              description: "30% down, remainder on completion",
              badge: "Recommended",
              badgeColor: "bg-emerald-100 text-emerald-800",
            },
            {
              id: "full-payment",
              label: "Full Payment",
              description: "Pay in full upon project completion",
              badge: null,
              badgeColor: null,
            },
          ].map((method) => (
            <label
              key={method.id}
              className={`flex items-start space-x-4 p-6 border-2 rounded-2xl cursor-pointer transition-all duration-300 ${
                installationData.paymentMethod === method.id
                  ? "border-emerald-500 bg-emerald-50 shadow-lg shadow-emerald-500/20"
                  : "border-gray-200 hover:border-gray-300 hover:shadow-sm"
              }`}
            >
              <input
                type="radio"
                name="paymentMethod"
                value={method.id}
                checked={installationData.paymentMethod === method.id}
                onChange={(e) => handleInputChange("paymentMethod", e.target.value)}
                className="mt-1"
              />
              <div className="flex-1">
                <div className="flex items-center space-x-3">
                  <span className="font-semibold text-lg text-gray-900">
                    {method.label}
                  </span>
                  {method.badge && (
                    <span
                      className={`text-xs px-2 py-1 rounded-full font-medium ${method.badgeColor}`}
                    >
                      {method.badge}
                    </span>
                  )}
                </div>
                <div className="text-gray-600 mt-1">{method.description}</div>
              </div>
            </label>
          ))}
        </div>
        {errors.paymentMethod && (
          <p className="text-red-500 text-sm mt-1">{errors.paymentMethod}</p>
        )}
      </div>

      {/* Payment Breakdown */}
      {installationData.paymentMethod === "payment-plan" && (
        <div className="bg-emerald-50 rounded-2xl p-6 border border-emerald-200">
          <h4 className="font-semibold text-emerald-900 mb-4">
            Payment Breakdown
          </h4>
          <div className="space-y-3">
            <div className="flex justify-between">
              <span className="text-emerald-700">Down Payment (30%):</span>
              <span className="font-semibold text-emerald-900 text-lg">
                $
                {(selectedContractor.selectedBid.price * 0.3).toLocaleString()}
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-emerald-700">Balance on Completion:</span>
              <span className="font-semibold text-emerald-900 text-lg">
                $
                {(selectedContractor.selectedBid.price * 0.7).toLocaleString()}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default PaymentMethodStep;
